/**
 * Markdown Editor
 * 
 * Provides markdown formatting with preview.
 * Two modes:
 * - 'simple': Basic formatting (bold, italic, lists, quotes, headings). No links. For guest book.
 * - 'full': Full markdown support including links, code, and [[quick-links]]. For admin.
 */

'use client'

import { useState, useRef, useCallback } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Quote, 
  Heading1,
  Heading2, 
  Heading3,
  Code,
  Link2,
  Eye, 
  Edit3,
  HelpCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { sanitizeGuestBookMarkdown } from '@/lib/markdown-sanitizer'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { cn } from '@/lib/utils'

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  maxLength?: number
  rows?: number
  className?: string
  id?: string
  required?: boolean
  /** 
   * Editor mode:
   * - 'simple': Basic formatting, no links (for guest book)
   * - 'full': Full markdown with links and quick-links (for admin)
   */
  mode?: 'simple' | 'full'
  /** Minimum height for the textarea/preview */
  minHeight?: string
  /** Maximum height for the textarea (useful in modals to prevent overflow) */
  maxHeight?: string
  /** Show character count */
  showCharCount?: boolean
  /** Show formatting help */
  showHelp?: boolean
  /** Hide the preview tab (useful when there's an external preview panel) */
  hidePreview?: boolean
}

export type { MarkdownEditorProps }

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  maxLength = 10000,
  rows = 5,
  className,
  id,
  required,
  mode = 'simple',
  minHeight,
  maxHeight,
  showCharCount = true,
  showHelp = false,
  hidePreview = false,
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write')
  const [helpExpanded, setHelpExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const textareaId = id || 'markdown-editor-textarea'

  // Get textarea element - uses ref first, falls back to getElementById
  const getTextarea = useCallback(() => {
    return textareaRef.current || document.getElementById(textareaId) as HTMLTextAreaElement | null
  }, [textareaId])

  const insertMarkdown = useCallback((before: string, after: string = '', defaultText: string = '') => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)
    const textToInsert = selectedText || defaultText

    const newText = 
      value.substring(0, start) + 
      before + 
      textToInsert + 
      after + 
      value.substring(end)

    onChange(newText)

    setTimeout(() => {
      const newCursorPos = start + before.length + textToInsert.length
      textarea.focus()
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }, [value, onChange, getTextarea])

  const insertAtLineStart = useCallback((prefix: string) => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const newText = value.substring(0, lineStart) + prefix + value.substring(lineStart)
    onChange(newText)
    
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(lineStart + prefix.length, lineStart + prefix.length)
    }, 0)
  }, [value, onChange, getTextarea])

  // Keyboard shortcuts handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMod = e.metaKey || e.ctrlKey
    
    if (!isMod) return

    switch (e.key.toLowerCase()) {
      case 'b':
        e.preventDefault()
        insertMarkdown('**', '**', 'bold text')
        break
      case 'i':
        e.preventDefault()
        insertMarkdown('*', '*', 'italic text')
        break
      case 'k':
        // Only in full mode
        if (mode === 'full') {
          e.preventDefault()
          insertMarkdown('[', '](url)', 'link text')
        }
        break
      case 'e':
        // Inline code
        e.preventDefault()
        insertMarkdown('`', '`', 'code')
        break
    }
  }, [insertMarkdown, mode])

  // Simple mode toolbar (guest book)
  const simpleToolbar = [
    { icon: Bold, action: () => insertMarkdown('**', '**', 'bold text'), title: 'Bold (⌘B)' },
    { icon: Italic, action: () => insertMarkdown('*', '*', 'italic text'), title: 'Italic (⌘I)' },
    { icon: Heading2, action: () => insertAtLineStart('## '), title: 'Heading' },
    { icon: List, action: () => insertAtLineStart('- '), title: 'Bullet List' },
    { icon: ListOrdered, action: () => insertAtLineStart('1. '), title: 'Numbered List' },
    { icon: Quote, action: () => insertAtLineStart('> '), title: 'Quote' },
  ]

  // Full mode toolbar (admin)
  const fullToolbar = [
    { icon: Bold, action: () => insertMarkdown('**', '**', 'bold text'), title: 'Bold (⌘B)' },
    { icon: Italic, action: () => insertMarkdown('*', '*', 'italic text'), title: 'Italic (⌘I)' },
    { type: 'divider' as const },
    { icon: Heading1, action: () => insertAtLineStart('# '), title: 'Heading 1' },
    { icon: Heading2, action: () => insertAtLineStart('## '), title: 'Heading 2' },
    { icon: Heading3, action: () => insertAtLineStart('### '), title: 'Heading 3' },
    { type: 'divider' as const },
    { icon: List, action: () => insertAtLineStart('- '), title: 'Bullet List' },
    { icon: ListOrdered, action: () => insertAtLineStart('1. '), title: 'Numbered List' },
    { icon: Quote, action: () => insertAtLineStart('> '), title: 'Blockquote' },
    { type: 'divider' as const },
    { icon: Code, action: () => insertMarkdown('`', '`', 'code'), title: 'Inline Code (⌘E)' },
    { icon: Link2, action: () => insertMarkdown('[', '](url)', 'link text'), title: 'Link (⌘K)' },
  ]

  const toolbar = mode === 'full' ? fullToolbar : simpleToolbar

  const renderSimplePreview = () => {
    if (!value.trim()) {
      return (
        <div className="text-zinc-500 italic p-3">
          Nothing to preview yet...
        </div>
      )
    }
    const sanitized = sanitizeGuestBookMarkdown(value)
    return (
      <div 
        className="prose prose-sm prose-invert max-w-none p-3"
        dangerouslySetInnerHTML={{ __html: sanitized.html }}
      />
    )
  }

  const renderFullPreview = () => {
    if (!value.trim()) {
      return (
        <div className="text-zinc-500 italic p-4 text-sm">
          Nothing to preview yet...
        </div>
      )
    }

    // Process [[quick-links]] before rendering
    const processedContent = processQuickLinks(value)

    return (
      <div className="p-4">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={{
            // Scale headings down for content within cards/modals
            h1: ({ children }) => (
              <h3 className="text-lg font-bold text-white mt-4 mb-2 first:mt-0">{children}</h3>
            ),
            h2: ({ children }) => (
              <h4 className="text-base font-semibold text-white mt-3 mb-2 first:mt-0">{children}</h4>
            ),
            h3: ({ children }) => (
              <h5 className="text-sm font-semibold text-zinc-200 mt-3 mb-1 first:mt-0">{children}</h5>
            ),
            p: ({ children }) => (
              <p className="text-sm text-zinc-300 mb-3 last:mb-0 leading-relaxed">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-inside text-sm text-zinc-300 mb-3 space-y-1 pl-2">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside text-sm text-zinc-300 mb-3 space-y-1 pl-2">{children}</ol>
            ),
            li: ({ children }) => (
              <li className="leading-relaxed">{children}</li>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-white">{children}</strong>
            ),
            em: ({ children }) => (
              <em className="italic">{children}</em>
            ),
            code: ({ className, children }) => {
              const isBlock = className?.includes('language-')
              if (isBlock) {
                return (
                  <code className="block bg-zinc-900 rounded-md p-3 text-xs font-mono text-zinc-300 overflow-x-auto mb-3">
                    {children}
                  </code>
                )
              }
              return (
                <code className="px-1.5 py-0.5 rounded bg-zinc-700 text-xs font-mono text-violet-300">
                  {children}
                </code>
              )
            },
            pre: ({ children }) => <pre className="mb-3">{children}</pre>,
            blockquote: ({ children }) => (
              <blockquote className="border-l-2 border-violet-500 pl-4 italic text-zinc-400 mb-3">
                {children}
              </blockquote>
            ),
            a: ({ href, children }) => (
              <a 
                href={href} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
              >
                {children}
              </a>
            ),
            hr: () => <hr className="border-zinc-700 my-4" />,
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    )
  }

  // Toolbar component (shared between both modes)
  const renderToolbar = () => (
    <div className="flex items-center gap-0.5 sm:gap-1 flex-wrap">
      {toolbar.map((btn, idx) =>
        'type' in btn && btn.type === 'divider' ? (
          <div key={`divider-${idx}`} className="w-px h-5 bg-zinc-700 mx-1 hidden sm:block" />
        ) : (
          <Button
            key={'title' in btn ? btn.title : `btn-${idx}`}
            type="button"
            variant="ghost"
            size="sm"
            onClick={'action' in btn ? btn.action : undefined}
            className={cn(
              "p-0 hover:bg-zinc-700",
              mode === 'full' ? "h-8 w-8" : "h-6 w-6 sm:h-7 sm:w-7"
            )}
            title={'title' in btn ? btn.title : undefined}
          >
            {'icon' in btn && <btn.icon className={cn(mode === 'full' ? "h-4 w-4" : "h-3 w-3 sm:h-3.5 sm:w-3.5")} />}
          </Button>
        )
      )}
    </div>
  )

  // Editor textarea and footer (shared between both modes)
  const renderEditor = () => (
    <>
      <Textarea
        ref={textareaRef}
        id={textareaId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        required={required}
        className={cn(
          "font-mono text-sm bg-zinc-800 border-zinc-700",
          mode === 'full' ? "resize-y" : "resize-none"
        )}
        style={{ 
          ...(minHeight && { minHeight }), 
          ...(maxHeight && { maxHeight }),
        }}
      />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-2 gap-1">
        {mode === 'simple' ? (
          <p className="text-xs text-zinc-500">
            <strong className="hidden sm:inline">Formatting:</strong>
            <span className="hidden sm:inline"> **bold**, *italic*, lists, quotes. Links not supported.</span>
            <span className="sm:hidden">**bold**, *italic*, lists, quotes</span>
          </p>
        ) : (
          <p className="text-xs text-zinc-500">
            Full Markdown supported. Use [[path]] for internal links.
          </p>
        )}
        {showCharCount && (
          <p className="text-xs text-zinc-500 shrink-0">
            {value.length.toLocaleString()}{maxLength < 100000 ? `/${maxLength.toLocaleString()}` : ''}
          </p>
        )}
      </div>
    </>
  )

  // Simple mode without preview tabs (for use with external preview panel)
  if (hidePreview) {
    return (
      <div className={cn("overflow-x-hidden", className)}>
        <div className="flex items-center justify-end mb-2 overflow-x-auto scrollbar-hide">
          {/* eslint-disable-next-line react-hooks/refs -- ref is only accessed in onClick handlers, not during render */}
          {renderToolbar()}
        </div>
        {renderEditor()}

        {/* Formatting & Link Syntax Help */}
        {showHelp && mode === 'full' && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setHelpExpanded(!helpExpanded)}
              className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-400 transition-colors"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              <span>Formatting & link syntax</span>
              {helpExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            
            {helpExpanded && (
              <div className="mt-3 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 text-xs space-y-4">
                <MarkdownSyntaxHelp />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Full mode with Write/Preview tabs
  return (
    <div className={cn("overflow-x-hidden", className)}>
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'write' | 'preview')} className="w-full">
        <div className="flex items-center justify-between mb-2 gap-2">
          <TabsList className={cn(
            "grid w-fit grid-cols-2 bg-zinc-800/50 shrink-0",
            mode === 'full' ? "" : ""
          )}>
            <TabsTrigger value="write" className={cn(
              "data-[state=active]:bg-zinc-700",
              mode === 'full' ? "text-sm px-4" : "text-xs px-2 sm:px-3"
            )}>
              <Edit3 className={cn(mode === 'full' ? "h-4 w-4 mr-2" : "h-3 w-3 sm:mr-1.5")} />
              <span className={mode === 'full' ? "" : "hidden sm:inline"}>Write</span>
            </TabsTrigger>
            <TabsTrigger value="preview" className={cn(
              "data-[state=active]:bg-zinc-700",
              mode === 'full' ? "text-sm px-4" : "text-xs px-2 sm:px-3"
            )}>
              <Eye className={cn(mode === 'full' ? "h-4 w-4 mr-2" : "h-3 w-3 sm:mr-1.5")} />
              <span className={mode === 'full' ? "" : "hidden sm:inline"}>Preview</span>
            </TabsTrigger>
          </TabsList>

          {/* Formatting toolbar - only show in write mode */}
          <div className="overflow-x-auto scrollbar-hide">
            {/* eslint-disable-next-line react-hooks/refs -- ref is only accessed in onClick handlers, not during render */}
            {activeTab === 'write' && renderToolbar()}
          </div>
        </div>

        <TabsContent value="write" className="mt-0">
          {renderEditor()}
        </TabsContent>

        <TabsContent value="preview" className="mt-0">
          <div 
            className="border border-zinc-700 rounded-md bg-zinc-900/50 overflow-y-auto"
            style={minHeight ? { minHeight } : { minHeight: '120px' }}
          >
            {mode === 'full' ? renderFullPreview() : renderSimplePreview()}
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            This is how your content will appear
          </p>
        </TabsContent>
      </Tabs>

      {/* Formatting & Link Syntax Help (for full mode) */}
      {showHelp && mode === 'full' && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setHelpExpanded(!helpExpanded)}
            className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-400 transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            <span>Formatting & link syntax</span>
            {helpExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          
          {helpExpanded && (
            <div className="mt-3 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 text-xs space-y-4">
              <MarkdownSyntaxHelp />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Process [[quick-links]] syntax into standard markdown links
 */
function processQuickLinks(content: string, artistHandle?: string): string {
  // Match [[path]] or [[path|label]] syntax
  const linkPattern = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g
  
  return content.replace(linkPattern, (_, path: string, label?: string) => {
    const resolvedPath = resolvePath(path.trim(), artistHandle)
    const linkLabel = label?.trim() || getDefaultLabel(path.trim())
    return `[${linkLabel}](${resolvedPath})`
  })
}

function resolvePath(path: string, artistHandle?: string): string {
  if (path.startsWith('/')) return path
  if (path.startsWith('http')) return path
  
  // Dashboard paths - prefix with artist handle
  if (path.startsWith('dashboard')) {
    if (!artistHandle) {
      console.warn(`MarkdownPreview: artistHandle required for dashboard link: ${path}`)
      return '#'
    }
    return `/${artistHandle}/${path}`
  }
  
  const shortcuts: Record<string, string> = {
    'home': '/',
    'discover': '/discover',
    'login': '/login',
    'signup': '/signup',
    'terms': '/terms',
    'privacy': '/privacy',
    'contact': '/contact',
    'how-it-works': '/how-it-works',
    'how-it-works/your-first-event': '/how-it-works/your-first-event',
    'how-it-works/street-performing': '/how-it-works/street-performing',
    'how-it-works/bars-restaurants': '/how-it-works/bars-restaurants',
    'how-it-works/private-events': '/how-it-works/private-events',
    'how-it-works/big-shows': '/how-it-works/big-shows',
    'how-it-works/venues': '/how-it-works/venues',
    'how-it-works/growing-your-fanbase': '/how-it-works/growing-your-fanbase',
  }
  
  if (shortcuts[path]) return shortcuts[path]
  return `/${path}`
}

function getDefaultLabel(path: string): string {
  const labels: Record<string, string> = {
    'home': 'Homepage',
    'discover': 'Discover page',
    'login': 'Login page',
    'signup': 'Signup page',
    'terms': 'Terms of Service',
    'privacy': 'Privacy Policy',
    'contact': 'Contact page',
    'how-it-works': 'Overview',
    'how-it-works/your-first-event': 'Your First Event',
    'how-it-works/street-performing': 'Street Performing',
    'how-it-works/bars-restaurants': 'Bars & Restaurants',
    'how-it-works/private-events': 'Private Events',
    'how-it-works/big-shows': 'Big Shows & Concerts',
    'how-it-works/venues': 'For Venues',
    'how-it-works/growing-your-fanbase': 'Growing Your Fanbase',
  }
  
  if (labels[path]) return labels[path]
  
  // Dashboard paths
  if (path.startsWith('dashboard')) {
    const segment = path.replace('dashboard/', '').replace('dashboard', '')
    if (!segment) return 'Dashboard'
    return segment.charAt(0).toUpperCase() + segment.slice(1)
  }
  
  const segments = path.split('/')
  const lastSegment = segments[segments.length - 1]
  return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' ')
}

/**
 * Standalone markdown preview component
 * Renders markdown with full features including quick-links
 * Use for previewing content outside the editor
 */
interface MarkdownPreviewProps {
  content: string
  /**
   * Artist handle for constructing dashboard links.
   * Required when content uses [[dashboard/...]] link syntax.
   */
  artistHandle?: string
  className?: string
}

export function MarkdownPreview({ content, artistHandle, className }: MarkdownPreviewProps) {
  if (!content.trim()) {
    return (
      <div className={cn("text-zinc-500 italic text-sm", className)}>
        No content yet...
      </div>
    )
  }

  const processedContent = processQuickLinks(content, artistHandle)

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          // Scale headings down for content within cards/modals
          h1: ({ children }) => (
            <h3 className="text-lg font-bold text-white mt-4 mb-2 first:mt-0">{children}</h3>
          ),
          h2: ({ children }) => (
            <h4 className="text-base font-semibold text-white mt-3 mb-2 first:mt-0">{children}</h4>
          ),
          h3: ({ children }) => (
            <h5 className="text-sm font-semibold text-zinc-200 mt-3 mb-1 first:mt-0">{children}</h5>
          ),
          p: ({ children }) => (
            <p className="text-sm text-zinc-300 mb-3 last:mb-0 leading-relaxed">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside text-sm text-zinc-300 mb-3 space-y-1 pl-2">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside text-sm text-zinc-300 mb-3 space-y-1 pl-2">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed">{children}</li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-white">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic">{children}</em>
          ),
          code: ({ className, children }) => {
            const isBlock = className?.includes('language-')
            if (isBlock) {
              return (
                <code className="block bg-zinc-900 rounded-md p-3 text-xs font-mono text-zinc-300 overflow-x-auto mb-3">
                  {children}
                </code>
              )
            }
            return (
              <code className="px-1.5 py-0.5 rounded bg-zinc-700 text-xs font-mono text-violet-300">
                {children}
              </code>
            )
          },
          pre: ({ children }) => <pre className="mb-3">{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-violet-500 pl-4 italic text-zinc-400 mb-3">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
            >
              {children}
            </a>
          ),
          hr: () => <hr className="border-zinc-700 my-4" />,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

/**
 * Markdown syntax help component
 */
function MarkdownSyntaxHelp() {
  return (
    <>
      <div>
        <h4 className="font-semibold text-zinc-300 mb-2">Markdown Formatting</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-zinc-400">
          <code className="text-zinc-500">**bold**</code>
          <span>→ <strong className="text-white">bold</strong></span>
          <code className="text-zinc-500">*italic*</code>
          <span>→ <em>italic</em></span>
          <code className="text-zinc-500">`code`</code>
          <span>→ <code className="px-1 py-0.5 rounded bg-zinc-700 text-violet-300">code</code></span>
          <code className="text-zinc-500">[text](url)</code>
          <span>→ external link</span>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-zinc-300 mb-2">Internal Links</h4>
        <p className="text-zinc-400 mb-2">
          Use <code className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-300">[[path]]</code> or{' '}
          <code className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-300">[[path|Label]]</code> for custom text.
        </p>
        
        <div className="space-y-3">
          <div>
            <h5 className="text-zinc-400 font-medium mb-1 uppercase tracking-wide" style={{ fontSize: '10px' }}>Public Pages</h5>
            <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 text-zinc-500">
              <code>[[home]]</code><span>Homepage</span>
              <code>[[discover]]</code><span>Discover page</span>
              <code>[[login]]</code><span>Login page</span>
              <code>[[signup]]</code><span>Signup page</span>
              <code>[[terms]]</code><span>Terms of Service</span>
              <code>[[privacy]]</code><span>Privacy Policy</span>
            </div>
          </div>
          
          <div>
            <h5 className="text-zinc-400 font-medium mb-1 uppercase tracking-wide" style={{ fontSize: '10px' }}>How It Works Pages</h5>
            <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 text-zinc-500">
              <code>[[how-it-works]]</code><span>Overview</span>
              <code>[[how-it-works/your-first-event]]</code><span>Your First Event</span>
              <code>[[how-it-works/street-performing]]</code><span>Street Performing</span>
              <code>[[how-it-works/bars-restaurants]]</code><span>Bars & Restaurants</span>
              <code>[[how-it-works/private-events]]</code><span>Private Events</span>
              <code>[[how-it-works/big-shows]]</code><span>Big Shows & Concerts</span>
              <code>[[how-it-works/growing-your-fanbase]]</code><span>Growing Your Fanbase</span>
              <code>[[how-it-works/venues]]</code><span>For Venues</span>
            </div>
          </div>

          <div>
            <h5 className="text-zinc-400 font-medium mb-1 uppercase tracking-wide" style={{ fontSize: '10px' }}>Dashboard Pages (requires artist context)</h5>
            <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 text-zinc-500">
              <code>[[dashboard]]</code><span>Dashboard home</span>
              <code>[[dashboard/pieces]]</code><span>Repertoire</span>
              <code>[[dashboard/events]]</code><span>Events</span>
              <code>[[dashboard/analytics]]</code><span>Analytics</span>
              <code>[[dashboard/settings]]</code><span>Settings</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
